import { useEffect, useState } from 'react';

// ---- Kuregraph Icon — XY axes + "K" right half (< shape) ----
// Clean: just the two axes and the two diagonals of K, no background clutter
export function KGIcon({ size = 32 }: { size?: number; color?: string }) {
  const s = size;
  const sw = s * 0.10;
  const axisW = sw * 0.65;
  const axisColor = 'rgba(255,255,255,0.22)';
  const idBase = `kg-${s}`;
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        {/* Vertical stem: cyan → emerald */}
        <linearGradient id={`${idBase}-v`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00d9ff" />
          <stop offset="100%" stopColor="#00e87a" />
        </linearGradient>
        {/* Upper arm: emerald → gold */}
        <linearGradient id={`${idBase}-u`} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#00e87a" />
          <stop offset="100%" stopColor="#ffd700" />
        </linearGradient>
        {/* Lower arm: gold → orange */}
        <linearGradient id={`${idBase}-d`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffd700" />
          <stop offset="100%" stopColor="#ff6b35" />
        </linearGradient>
      </defs>

      {/* X-axis */}
      <line x1="3" y1="16" x2="29" y2="16"
        stroke={axisColor} strokeWidth={axisW} strokeLinecap="round" />
      {/* Y-axis */}
      <line x1="10" y1="29" x2="10" y2="3"
        stroke={axisColor} strokeWidth={axisW} strokeLinecap="round" />

      {/* K vertical stem */}
      <line x1="10" y1="4" x2="10" y2="28"
        stroke={`url(#${idBase}-v)`} strokeWidth={sw} strokeLinecap="round" />
      {/* K upper arm */}
      <line x1="10" y1="16" x2="26" y2="4"
        stroke={`url(#${idBase}-u)`} strokeWidth={sw} strokeLinecap="round" />
      {/* K lower arm */}
      <line x1="10" y1="16" x2="26" y2="28"
        stroke={`url(#${idBase}-d)`} strokeWidth={sw} strokeLinecap="round" />
    </svg>
  );
}

interface Props {
  onFinished: () => void;
}

// Each character gets a staggered reveal
const TITLE = 'kuregraph.';
const SUB   = 'Made by kurekure_botti';

export default function SplashScreen({ onFinished }: Props) {
  const [phase, setPhase] = useState<'enter' | 'hold' | 'exit'>('enter');
  const [charsDone, setCharsDone] = useState(false);
  const [subDone, setSubDone] = useState(false);

  // After all chars animated → short hold → exit
  useEffect(() => {
    const charDelay = TITLE.length * 80 + 700;
    const subDelay  = charDelay + 200;
    const holdDelay = subDelay + 900;
    const exitDelay = holdDelay + 100;
    // onFinished called AFTER fade-out completes (0.65s)
    const doneDelay = exitDelay + 680;

    const t1 = setTimeout(() => setCharsDone(true), charDelay);
    const t2 = setTimeout(() => setSubDone(true),   subDelay);
    const t3 = setTimeout(() => setPhase('hold'),   holdDelay);
    const t4 = setTimeout(() => setPhase('exit'),   exitDelay);
    const t5 = setTimeout(() => onFinished(),       doneDelay);

    return () => { [t1, t2, t3, t4, t5].forEach(clearTimeout); };
  }, [onFinished]);

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center select-none"
      style={{
        zIndex: 9999,
        background: '#05050f',
        opacity: phase === 'exit' ? 0 : 1,
        transition: phase === 'exit' ? 'opacity 0.65s cubic-bezier(0.4,0,0.2,1)' : 'none',
        pointerEvents: phase === 'exit' ? 'none' : 'all',
      }}
    >
      {/* Ambient glow blobs */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: 520, height: 520,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,200,255,0.12) 0%, transparent 70%)',
          top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          animation: 'splashGlowPulse 3s ease-in-out infinite',
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          width: 300, height: 300,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(140,60,255,0.10) 0%, transparent 70%)',
          top: '40%', left: '55%',
          transform: 'translate(-50%,-50%)',
          animation: 'splashGlowPulse 4s ease-in-out infinite reverse',
        }}
      />

      {/* Scanline effect */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.06) 2px, rgba(0,0,0,0.06) 4px)',
          zIndex: 1,
        }}
      />

      <div className="relative z-10 flex flex-col items-center gap-6">

        {/* Icon — colorful K on a coordinate system */}
        <div
          style={{
            animation: 'splashIconIn 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards',
            opacity: 0,
          }}
        >
          <KGIcon size={56} />
        </div>

        {/* Title — char-by-char reveal */}
        <div className="flex items-end gap-0" style={{ paddingBottom: '0.35em', overflow: 'visible' }}>
          {TITLE.split('').map((ch, i) => (
            <span
              key={i}
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: ch === '.' ? '2.8rem' : '3.2rem',
                letterSpacing: '-0.02em',
                lineHeight: 1,
                color: ch === '.' ? 'rgba(0,217,255,0.7)' : '#ffffff',
                display: 'inline-block',
                opacity: 0,
                transform: 'translateY(18px)',
                transformOrigin: 'bottom center',
                animation: `splashCharIn 0.45s cubic-bezier(0.34,1.56,0.64,1) ${i * 0.07 + 0.15}s forwards`,
                textShadow: ch === '.' ? '0 0 16px rgba(0,217,255,0.5)' : 'none',
              }}
            >
              {ch}
            </span>
          ))}
        </div>

        {/* Subtitle — slide up */}
        <div
          style={{
            opacity: subDone ? 1 : 0,
            transform: subDone ? 'translateY(0)' : 'translateY(10px)',
            transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '0.78rem',
              fontWeight: 500,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.55)',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
            }}
          >
            {SUB}
          </p>
        </div>

        {/* Loading bar */}
        <div
          className="mt-2 relative overflow-hidden rounded-full"
          style={{
            width: 160,
            height: 2,
            background: 'rgba(255,255,255,0.08)',
            opacity: charsDone ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, transparent, #00d9ff, transparent)',
              animation: charsDone ? 'splashBarScan 0.9s ease-in-out 1 forwards' : 'none',
            }}
          />
        </div>
      </div>
    </div>
  );
}
